import { usePasswordResetModalPresenter } from '@Components/Modals/PasswordResetModal/PasswordResetModalPresenter';
import useSmartBrowserPresenter from '@Components/NavigationContentDrawer/Panels/PrimaryNavigationColumn/SmartBrowser/SmartBrowser.presenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useDimensions } from 'react-native-web-hooks';

import OwnableTheme from '../../../assets/OwnableTheme';
import TextInput from '../../Common/TextInput';
import {
  StyledButtonContainer,
  StyledFormContainer,
  StyledSmartHeaderContainer,
} from './SmartBrowserFormModal.common';

import toastStore from '~stores/toast';

const CONDENSED_LIMIT = 745;

type Props = {
  onEmailTextInputLayout?: (event: LayoutChangeEvent) => void;
  onEmailTextInputFocus?: () => void;
  onPasswordTextInputLayout?: (event: LayoutChangeEvent) => void;
  onPasswordTextInputFocus?: () => void;
};

const SmartBrowserFormModalContentSignIn: FC<Props> = ({
  onEmailTextInputLayout,
  onEmailTextInputFocus,
  onPasswordTextInputLayout,
  onPasswordTextInputFocus,
}) => {
  const {
    setEmailAddress,
    setPassword,
    emailAddress,
    password,
    handleLoginSubmit,
    authError,
    setSignUpMode,
    handleCloseModal,
  } = useSmartBrowserPresenter();

  const { height: windowHeight } = useDimensions().window;

  const lowerButtonsMarginTop = windowHeight > CONDENSED_LIMIT ? 45 : 0;
  const marginTopForm = windowHeight > CONDENSED_LIMIT ? 45 : 0;

  const { show: showPasswordResetModal, requestPasswordReset } =
    usePasswordResetModalPresenter();

  const onPasswordReset = async () => {
    if (!emailAddress) {
      toastStore.addMessage({
        message: 'Please provide your email',
      });
      return;
    }
    try {
      await requestPasswordReset(emailAddress);
      handleCloseModal();
      showPasswordResetModal();
    } catch {}
  };

  return (
    <>
      <StyledSmartHeaderContainer>
        <Text style={styles.header}>
          You need to be signed in{'\n'} to review the things you{'\n'} have
          discovered or purchased.
        </Text>
      </StyledSmartHeaderContainer>
      <StyledFormContainer marginTop={marginTopForm}>
        <TextInput
          onLayout={onEmailTextInputLayout}
          onFocus={onEmailTextInputFocus}
          gutterBottom={25}
          fontSizePx={20}
          placeholder='Email'
          borderBottomColor={OwnableTheme.colors.grayInput}
          bgColor='white'
          value={emailAddress}
          onChange={(text) => {
            setEmailAddress(text);
          }}
          textAlign='center'
          color={OwnableTheme.colors.black}
          placeholderColor={OwnableTheme.colors.gray}
        />
        <TextInput
          color={OwnableTheme.colors.black}
          onLayout={onPasswordTextInputLayout}
          onFocus={onPasswordTextInputFocus}
          gutterBottom={16}
          secureTextEntry
          fontSizePx={20}
          placeholder='Password'
          borderBottomColor={OwnableTheme.colors.grayInput}
          value={password}
          bgColor='white'
          onChange={(text) => {
            setPassword(text);
          }}
          placeholderColor={OwnableTheme.colors.gray}
          textAlign='center'
        />
        {/* @ts-expect-error */}
        <Text size='xxs' align='center' color={OwnableTheme.colors.errorRed}>
          {authError ? authError : ' '}
        </Text>
        <TouchableOpacity onPress={handleLoginSubmit}>
          <StyledButtonContainer>
            <Text style={styles.buttonText}>Sign In</Text>
          </StyledButtonContainer>
        </TouchableOpacity>
        <View style={{ marginTop: lowerButtonsMarginTop }}>
          <TouchableOpacity onPress={onPasswordReset}>
            <StyledButtonContainer>
              <Text style={styles.buttonText}>Reset my password</Text>
            </StyledButtonContainer>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              setSignUpMode(true);
            }}
          >
            <StyledButtonContainer>
              <Text style={styles.buttonText}>I would like an account</Text>
            </StyledButtonContainer>
          </TouchableOpacity>
        </View>
      </StyledFormContainer>
    </>
  );
};

export default observer(SmartBrowserFormModalContentSignIn);

const styles = StyleSheet.create({
  header: {
    fontFamily: OwnableTheme.fonts.gilroy.bold,
    color: OwnableTheme.colors.black,
    fontSize: 24,
    textAlign: 'center',
  },
  buttonText: {
    color: OwnableTheme.colors.white,
    fontSize: 20,
  },
});
